import * as React from 'react';
import { root } from '@estee/elc-universal-utils';

export interface IState {
    config: object | null;
}
export interface IProps {
    config: object;
    mergeConfigs(stateConfig: object, propsConfig: object): object;
}

export function config<ComponentProps>(
    configs: string[],
    mergeConfigs = (stateConfig: object, propsConfig: object) => ({
        ...stateConfig,
        ...propsConfig
    })
) {
    if (root.registerConfigFieldsToPreload) {
        root.registerConfigFieldsToPreload(configs);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any => {
        return class extends React.Component<IProps, IState> {
            static displayName = 'config';
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            constructor(props: any) {
                super(props);

                const configsFromCache = root.getPreloadedConfigs
                    ? root.getPreloadedConfigs(configs)
                    : null;

                this.state = {
                    config: configsFromCache
                };
            }

            private isSSR() {
                return !!root.__ssr;
            }

            private hasConfigsRequested() {
                return configs && configs.length > 0;
            }

            public async componentDidMount() {
                // Only request configs if they're not already available
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const componentRenderer = root.ComponentRenderer as any;
                // App could not be using component renderer
                const preloader = componentRenderer ? componentRenderer.preloader : null;

                if (this.hasConfigsRequested() && !this.isSSR() && !this.state.config) {
                    let configResponse = null;

                    if (preloader && preloader.preload) {
                        await preloader.preload();
                        configResponse = preloader.getPreloadedConfigs(configs);
                    } else {
                        const { GlobalServiceBus, ServiceBusTopics, addConfigs } = root;

                        configResponse = await GlobalServiceBus.query(
                            ServiceBusTopics.queries.GET_CONFIG,
                            configs
                        );

                        if (addConfigs) {
                            addConfigs(configResponse);
                        }
                    }
                    this.setState({
                        config: configResponse
                    });
                }
            }

            public render() {
                const newConfig = mergeConfigs(this.state.config || {}, this.props.config);
                if (this.hasConfigsRequested() && !this.state.config) {
                    return null;
                }

                return <WrappedComponent {...this.props} config={newConfig} />;
            }
        };
    };
}

export function mockConfig<ComponentProps>(
    configs: string[],
    mergeConfigs = (stateConfig: object, propsConfig: object) => ({
        ...stateConfig,
        ...propsConfig
    })
) {
    const baseConfigs = configs.reduce(
        (allConfigs: object, currentConfig: string) => ({
            ...allConfigs,
            [currentConfig]: currentConfig
        }),
        {}
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any => {
        return class extends React.Component<IProps, IState> {
            public render() {
                const { config: mockedConfigs, ...otherProps } = this.props;

                const combinedConfig = {
                    ...baseConfigs,
                    ...mockedConfigs
                };

                return <WrappedComponent {...otherProps} config={combinedConfig} />;
            }
        };
    };
}
